import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as llmConnectionsApi from '@/api/llm-connections/api';
import type {
  CitationDTO,
  LlmFooterStateDTO,
  LlmProviderAgentDTO,
  LlmProviderKey,
  SummaryDetail,
} from '@/api/llm-connections/api';
import { useNoteSaving } from '@/hooks/useNoteSaving';
export type { CitationDTO, LlmFooterStateDTO, LlmProviderAgentDTO, LlmProviderKey, SummaryDetail };

/** A single message in the floating chat panel conversation history. */
export interface ChatHistoryItem {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  citations?: CitationDTO[];
}

/** Current streaming phase of the chat request. */
export type ChatPhase = 'idle' | 'retrieving' | 'generating';

type ProviderModelSelectionMap = Partial<Record<LlmProviderKey, string | null>>;

const tokenErrorCodes = new Set([
  'LLM_KEY_EXPIRED',
  'LLM_KEY_INVALID',
  'LLM_OAUTH_REAUTH_REQUIRED',
  'LLM_CONNECTION_UNAUTHORIZED',
]);
const providerConfigErrorCodes = new Set(['LLM_PROVIDER_NOT_CONFIGURED']);
const rateLimitErrorCodes = new Set(['LLM_RATE_LIMITED']);
const quotaErrorCodes = new Set(['LLM_PROVIDER_QUOTA_EXCEEDED']);
const agentErrorCodes = new Set(['LLM_AGENT_REQUIRED', 'LLM_AGENT_NOT_FOUND']);
const PROVIDERS_REQUIRING_MODEL = new Set<LlmProviderKey>([
  'copilot',
  'cursor',
  'aws_bedrock_platform',
  'aws_bedrock',
]);
const quickSettingOrder: LlmProviderKey[] = [
  'aws_bedrock_platform',
  'aws_bedrock',
  'cursor',
  'claude',
  'codex',
  'copilot',
];
const providerSortIndex = new Map<LlmProviderKey, number>(
  quickSettingOrder.map((provider, index) => [provider, index]),
);

export const providerRequiresModel = (provider: LlmProviderKey): boolean =>
  PROVIDERS_REQUIRING_MODEL.has(provider);

export interface UseChatFooterResult {
  // Provider / model state
  footerState: LlmFooterStateDTO | null;
  selectedProvider: LlmProviderKey | null;
  selectedModel: string | null;
  connectedProviders: LlmFooterStateDTO['providers'];
  availableModelsByProvider: LlmFooterStateDTO['agents'];
  selectedProviderModels: LlmProviderAgentDTO[];
  selectedAgentLabel: string | null;
  modelSelectPlaceholder: string;
  // UI state
  isLoading: boolean;
  isSaving: boolean;
  isSending: boolean;
  needsReconnect: boolean;
  chatPhase: ChatPhase;
  isPanelOpen: boolean;
  // Chat state
  chatDraft: string;
  chatHistory: ChatHistoryItem[];
  summaryDetail: SummaryDetail;
  savedNoteIds: Set<number>;
  savingNoteId: number | null;
  canSend: boolean;
  // Actions
  setChatDraft: (value: string) => void;
  setSummaryDetail: (value: SummaryDetail) => void;
  setPanelOpen: (open: boolean) => void;
  loadFooterState: () => Promise<void>;
  ensureModelsLoaded: (provider: LlmProviderKey) => Promise<LlmProviderAgentDTO[]>;
  onProviderChange: (provider: LlmProviderKey) => Promise<void>;
  onModelChange: (modelId: string | null) => Promise<void>;
  onSend: () => Promise<void>;
  onSaveAsNote: (item: ChatHistoryItem) => Promise<void>;
}

/**
 * Manages LLM provider selection, chat streaming, and panel state for the floating
 * chat side panel. Controlled panel visibility is supported by passing `chatPanelOpen`
 * and `onChatPanelOpenChange`; omit both for fully internal state.
 *
 * Note saving is delegated to useNoteSaving. Provider selection and chat streaming
 * remain together because onSend both reads selectedProvider/selectedModel (provider
 * concerns) and updates them from the response — bidirectional coupling that would
 * require shared setters and adds more complexity than it removes.
 */
export function useChatFooter(
  accessToken: string,
  clientId: string,
  projectId: string,
  chatPanelOpen: boolean | undefined,
  onChatPanelOpenChange: ((open: boolean) => void) | undefined,
  onCreateNoteNavigate: ((assetSessionId: string) => void) | undefined,
): UseChatFooterResult {
  const [footerState, setFooterState] = useState<LlmFooterStateDTO | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<LlmProviderKey | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [modelSelectionByProvider, setModelSelectionByProvider] =
    useState<ProviderModelSelectionMap>({});
  const [chatDraft, setChatDraft] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [internalPanelOpen, setInternalPanelOpen] = useState<boolean>(false);
  const [needsReconnect, setNeedsReconnect] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [chatPhase, setChatPhase] = useState<ChatPhase>('idle');
  const [summaryDetail, setSummaryDetail] = useState<SummaryDetail>('concise');
  const { savedNoteIds, savingNoteId, onSaveAsNote } = useNoteSaving({
    accessToken,
    clientId,
    projectId,
    onCreateNoteNavigate,
  });
  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageCounterRef = useRef<number>(1);
  const isFetchingFooterRef = useRef<boolean>(false);

  const isPanelOpen = chatPanelOpen ?? internalPanelOpen;

  const setPanelOpen = useCallback(
    (open: boolean) => {
      onChatPanelOpenChange?.(open);
      if (chatPanelOpen === undefined) {
        setInternalPanelOpen(open);
      }
    },
    [chatPanelOpen, onChatPanelOpenChange],
  );

  const appendChatMessage = useCallback(
    (role: ChatHistoryItem['role'], text: string, citations?: CitationDTO[]) => {
      const id = messageCounterRef.current;
      messageCounterRef.current += 1;
      setChatHistory((previous) => [...previous, { id, role, text, citations }]);
    },
    [],
  );

  const handleApiError = useCallback((error_: unknown) => {
    const fallback = error_ instanceof Error ? error_.message : 'Request failed';
    if (!(error_ instanceof llmConnectionsApi.LlmApiError)) {
      void message.error(fallback);
      return;
    }
    if (error_.code && rateLimitErrorCodes.has(error_.code)) {
      void message.warning(
        'Provider rate limit reached. Try again shortly or choose another connected provider.',
      );
      return;
    }
    if (error_.code && quotaErrorCodes.has(error_.code)) {
      void message.warning(
        'Provider quota exceeded for this API key. Add billing/credits or choose another connected provider.',
      );
      return;
    }
    if (error_.code && providerConfigErrorCodes.has(error_.code)) {
      if (error_.message.includes('CURSOR_DEFAULT_REPOSITORY_URL')) {
        void message.warning(error_.message);
        return;
      }
      void message.warning(
        `${error_.message}. Contact an administrator to configure this provider.`,
      );
      return;
    }
    if (error_.code && tokenErrorCodes.has(error_.code)) {
      setNeedsReconnect(true);
      void message.warning(
        `${error_.message}. Contact an administrator to reconnect this provider.`,
      );
      return;
    }
    if (error_.code && agentErrorCodes.has(error_.code)) {
      void message.warning(
        `${error_.message}. Choose a model for this provider in quick settings.`,
      );
      return;
    }
    void message.error(error_.message);
  }, []);

  const loadFooterState = useCallback(async () => {
    if (isFetchingFooterRef.current) {
      return;
    }
    isFetchingFooterRef.current = true;
    setIsLoading(true);
    try {
      const data = await llmConnectionsApi.getLlmFooterState(accessToken, clientId, projectId);
      setFooterState(data);
      setSelectedProvider(data.active_provider);
      setSelectedModel(data.active_agent_id);
      setModelSelectionByProvider((previous) => {
        const next: ProviderModelSelectionMap = { ...previous };
        if (data.active_provider) {
          next[data.active_provider] = data.active_agent_id;
        }
        return next;
      });
      setNeedsReconnect(false);
    } catch (error_) {
      handleApiError(error_);
    } finally {
      setIsLoading(false);
      isFetchingFooterRef.current = false;
    }
  }, [accessToken, clientId, projectId, handleApiError]);

  useEffect(() => {
    void loadFooterState();
  }, [loadFooterState]);

  const connectedProviders = useMemo(
    () =>
      (footerState?.providers ?? [])
        .filter((provider) => provider.connected)
        .sort(
          (a, b) =>
            (providerSortIndex.get(a.provider) ?? 100) - (providerSortIndex.get(b.provider) ?? 100),
        ),
    [footerState],
  );

  const availableModelsByProvider = useMemo(() => footerState?.agents ?? {}, [footerState]);

  const selectedAgentLabel = useMemo(() => {
    if (!selectedProvider || !selectedModel) {
      return null;
    }
    const options = availableModelsByProvider[selectedProvider] ?? [];
    const match = options.find((item) => item.agent_id === selectedModel);
    return match?.label ?? selectedModel;
  }, [availableModelsByProvider, selectedModel, selectedProvider]);

  const persistSelection = useCallback(
    async (provider: LlmProviderKey, modelId: string | null) => {
      const providerState = connectedProviders.find((item) => item.provider === provider);
      if (!providerState) {
        void message.warning(
          'Selected provider is unavailable. Contact an administrator to reconnect it.',
        );
        return;
      }
      setIsSaving(true);
      try {
        setModelSelectionByProvider((previous) => ({
          ...previous,
          [provider]: modelId,
        }));
        await llmConnectionsApi.saveLlmFooterSelection(accessToken, clientId, projectId, {
          provider,
          connection_id: providerState.connection_id ?? null,
          agent_id: modelId,
        });
        await loadFooterState();
      } catch (error_) {
        handleApiError(error_);
        await loadFooterState();
      } finally {
        setIsSaving(false);
      }
    },
    [accessToken, clientId, projectId, connectedProviders, loadFooterState, handleApiError],
  );

  const ensureModelsLoaded = useCallback(
    async (provider: LlmProviderKey): Promise<LlmProviderAgentDTO[]> => {
      const existing = availableModelsByProvider[provider];
      if (existing && existing.length > 0) {
        return existing;
      }
      try {
        const connectionId =
          connectedProviders.find((item) => item.provider === provider)?.connection_id ?? undefined;
        const response = await llmConnectionsApi.listLlmProviderAgents(
          accessToken,
          provider,
          connectionId,
        );
        setFooterState((previous) => {
          if (!previous) {
            return previous;
          }
          return {
            ...previous,
            agents: { ...previous.agents, [provider]: response.agents },
          };
        });
        return response.agents;
      } catch (error_) {
        handleApiError(error_);
        return [];
      }
    },
    [accessToken, availableModelsByProvider, connectedProviders, handleApiError],
  );

  const selectedProviderModels = useMemo(
    () => (selectedProvider ? (availableModelsByProvider[selectedProvider] ?? []) : []),
    [availableModelsByProvider, selectedProvider],
  );

  const modelSelectPlaceholder = useMemo(() => {
    if (!selectedProvider) {
      return 'Select provider';
    }
    if (selectedProviderModels.length > 0) {
      return 'Model';
    }
    const bedrockProviderKeys = new Set<LlmProviderKey>(['aws_bedrock', 'aws_bedrock_platform']);
    return bedrockProviderKeys.has(selectedProvider)
      ? 'No models match active profiles'
      : 'No model';
  }, [selectedProvider, selectedProviderModels.length]);

  const onProviderChange = useCallback(
    async (provider: LlmProviderKey) => {
      const providerModels = await ensureModelsLoaded(provider);
      const rememberedModel = modelSelectionByProvider[provider] ?? null;
      const rememberedModelIsAvailable =
        !!rememberedModel && providerModels.some((item) => item.agent_id === rememberedModel);
      let nextModel: string | null = null;
      if (rememberedModelIsAvailable) {
        nextModel = rememberedModel;
      } else if (providerModels.length > 0) {
        nextModel = providerModels[0].agent_id;
      }
      setSelectedProvider(provider);
      setSelectedModel(nextModel);
      setModelSelectionByProvider((previous) => ({
        ...previous,
        [provider]: nextModel,
      }));
      if (providerRequiresModel(provider) && !nextModel) {
        void message.warning('Model selection is required for this provider.');
        return;
      }
      await persistSelection(provider, nextModel);
    },
    [ensureModelsLoaded, modelSelectionByProvider, persistSelection],
  );

  const onModelChange = useCallback(
    async (modelId: string | null) => {
      if (!selectedProvider) {
        void message.warning('Select a connected provider before choosing a model.');
        return;
      }
      if (providerRequiresModel(selectedProvider) && !modelId) {
        void message.warning('Model selection is required for this provider.');
        return;
      }
      setSelectedModel(modelId);
      setModelSelectionByProvider((previous) => ({
        ...previous,
        [selectedProvider]: modelId,
      }));
      await persistSelection(selectedProvider, modelId);
    },
    [persistSelection, selectedProvider],
  );

  useEffect(() => {
    if (!selectedProvider) {
      return;
    }
    void ensureModelsLoaded(selectedProvider);
  }, [ensureModelsLoaded, selectedProvider]);

  const canSend = useMemo(() => {
    if (!selectedProvider || !chatDraft.trim() || isLoading || isSending || isSaving) {
      return false;
    }
    if (providerRequiresModel(selectedProvider) && !selectedModel) {
      return false;
    }
    return true;
  }, [selectedProvider, selectedModel, chatDraft, isLoading, isSending, isSaving]);

  const onSend = useCallback(async () => {
    const text = chatDraft.trim();
    if (!text) {
      return;
    }
    if (!selectedProvider) {
      void message.warning('Select a connected provider before sending.');
      return;
    }
    if (providerRequiresModel(selectedProvider) && !selectedModel) {
      void message.warning('Model selection is required for this provider.');
      return;
    }
    appendChatMessage('user', text);
    setChatDraft('');
    setIsSending(true);
    setChatPhase('retrieving');
    phaseTimerRef.current = setTimeout(() => {
      setChatPhase('generating');
    }, 800);
    try {
      const historyPayload = chatHistory.map((item) => ({
        role: item.role,
        content: item.text,
      }));
      const response = await llmConnectionsApi.sendChatMessageViaEvents(
        accessToken,
        clientId,
        projectId,
        {
          message: text,
          provider_override: selectedProvider,
          agent_override: selectedModel,
          knowledge_enabled: true,
          history: historyPayload,
          summary_detail: summaryDetail,
        },
      );
      setSelectedProvider(response.provider);
      setSelectedModel(response.agent_id ?? null);
      setModelSelectionByProvider((previous) => ({
        ...previous,
        [response.provider]: response.agent_id ?? null,
      }));
      appendChatMessage(
        'assistant',
        response.reply,
        response.citations?.length ? response.citations : undefined,
      );
    } catch (error_) {
      handleApiError(error_);
    } finally {
      if (phaseTimerRef.current) {
        clearTimeout(phaseTimerRef.current);
        phaseTimerRef.current = null;
      }
      setChatPhase('idle');
      setIsSending(false);
    }
  }, [
    accessToken,
    appendChatMessage,
    chatDraft,
    chatHistory,
    clientId,
    handleApiError,
    projectId,
    selectedModel,
    selectedProvider,
    summaryDetail,
  ]);

  return {
    footerState,
    selectedProvider,
    selectedModel,
    connectedProviders,
    availableModelsByProvider,
    selectedProviderModels,
    selectedAgentLabel,
    modelSelectPlaceholder,
    isLoading,
    isSaving,
    isSending,
    needsReconnect,
    chatPhase,
    isPanelOpen,
    chatDraft,
    chatHistory,
    summaryDetail,
    savedNoteIds,
    savingNoteId,
    canSend,
    setChatDraft,
    setSummaryDetail,
    setPanelOpen,
    loadFooterState,
    ensureModelsLoaded,
    onProviderChange,
    onModelChange,
    onSend,
    onSaveAsNote,
  };
}
